import { useMemo } from "react";
import {
  Avatar,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { CalendarIcon } from "@heroicons/react/20/solid";
import type { AssociatedObject } from "./types";
import { getMeetingViewModel, safeDate } from "./meetingUtils";

type MeetingListItemProps = {
  meeting: AssociatedObject;
  onOpen: (meeting: AssociatedObject) => void;
};

function formatListTime(start: Date | null, end: Date | null): string {
  if (!start) return "—";

  const now = new Date();
  const isToday =
    start.getFullYear() === now.getFullYear()
    && start.getMonth() === now.getMonth()
    && start.getDate() === now.getDate();

  if (!end) {
    const startTime = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const startDate = start.toLocaleDateString([], { day: "numeric", month: "short" });
    return isToday ? `Today, ${startTime}` : `${startDate} • ${startTime}`;
  }

  const sameDay =
    start.getFullYear() === end.getFullYear()
    && start.getMonth() === end.getMonth()
    && start.getDate() === end.getDate();

  const startTime = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const endTime = end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const startDate = start.toLocaleDateString([], { day: "numeric", month: "short" });
  const endDate = end.toLocaleDateString([], { day: "numeric", month: "short" });

  if (isToday && sameDay) return `Today, ${startTime} – ${endTime}`;
  if (sameDay) return `${startDate} • ${startTime} – ${endTime}`;
  return `${startDate} • ${startTime} – ${endDate} • ${endTime}`;
}

const MeetingListItem = ({ meeting, onOpen }: MeetingListItemProps) => {
  const view = useMemo(() => getMeetingViewModel(meeting), [meeting]);

  const startDate = useMemo(() => safeDate(view.startTime), [view.startTime]);
  const endDate = useMemo(() => safeDate(view.endTime), [view.endTime]);
  const isUpcoming = useMemo(() => (startDate ? startDate > new Date() : false), [startDate]);
  const timeLabel = useMemo(() => formatListTime(startDate, endDate), [startDate, endDate]);

  const participants = view.participants.slice(0, 3);
  const extraParticipants = Math.max(0, view.participants.length - participants.length);
  const remainingParticipantsLabel = useMemo(() => {
    const remaining = view.participants.slice(participants.length);
    if (remaining.length === 0) return "";
    const preview = remaining.slice(0, 8).join(", ");
    if (remaining.length <= 8) return preview;
    return `${preview} +${remaining.length - 8} more`;
  }, [participants.length, view.participants]);

  return (
    <UnstyledButton onClick={() => onOpen(meeting)} className="w-full">
      <Group
        gap="sm"
        wrap="nowrap"
        className={`px-4 py-3 hover:bg-gray-50 transition-colors ${isUpcoming ? "border-l-4 border-purple-400" : "border-l-4 border-transparent"}`}
      >
        <ThemeIcon variant="light" color="main" radius="xl" size={36}>
          <CalendarIcon className="h-5 w-5" />
        </ThemeIcon>

        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
            <Text fw={600} c="text.9" size="sm" lineClamp={1} style={{ flex: 1 }}>
              {view.title}
            </Text>
            <Badge variant="light" color={isUpcoming ? "main" : "gray"} radius="md" size="sm">
              {isUpcoming ? "Upcoming" : "Past"}
            </Badge>
          </Group>

          <Text size="xs" c="text.5" lineClamp={1}>
            {timeLabel}
          </Text>

          <Group gap={8} wrap="nowrap" justify="space-between">
            <Group gap={6} wrap="nowrap">
              <Group gap={-6} wrap="nowrap">
                {participants.map((participant: string) => (
                  <Tooltip key={participant} label={participant} withArrow>
                    <Avatar className="z-10" radius="xl" name={participant} ml={-6} size={20} variant="filled" color="initials">
                      {participant.slice(0, 1).toUpperCase()}
                    </Avatar>
                  </Tooltip>
                ))}
                {extraParticipants > 0 && (
                  <Tooltip label={remainingParticipantsLabel || `${extraParticipants} more`} withArrow multiline w={260}>
                    <Avatar radius="xl" size={20} variant="outline" color="main">
                      +{extraParticipants}
                    </Avatar>
                  </Tooltip>
                )}
              </Group>
              {view.participants.length > 0 && (
                <Text size="xs" c="text.5">
                  {view.participants.length} attendee{view.participants.length === 1 ? "" : "s"}
                </Text>
              )}
            </Group>

            {view.meetingUrl && isUpcoming && (
              <Button
                component="a"
                href={view.meetingUrl}
                target="_blank"
                rel="noreferrer"
                size="xs"
                variant="light"
                color="main"
                onClick={(e) => e.stopPropagation()}
              >
                Join
              </Button>
            )}
          </Group>
        </Stack>
      </Group>
    </UnstyledButton>
  );
};

export default MeetingListItem;
